import React, { useEffect, useMemo, useRef, useState } from "react";
import useGameBase from "../../hooks/useGameBase";
import SpectatorPerspectiveSelector from "./SpectatorPerspectiveSelector";

const SUIT_SYMBOLS = {
  hearts: "♥",
  diamonds: "♦",
  clubs: "♣",
  spades: "♠",
};

function isWild(card) {
  return !!card && (card.rank === "2" || card.rank === "joker");
}

function isRedThree(card) {
  return !!card && card.rank === "3" && (card.suit === "hearts" || card.suit === "diamonds");
}

function isBlackThree(card) {
  return !!card && card.rank === "3" && (card.suit === "clubs" || card.suit === "spades");
}

function getCardLabel(card) {
  if (!card) return "";
  if (card.rank === "joker") return `${card.jokerColor || "red"} joker`;
  return `${card.rank} of ${card.suit}`;
}

function getCardImage(card) {
  if (!card) return "/cards/card_back.png";
  if (card.code) return `/cards/${card.code}`;
  if (card.rank === "joker") {
    return `/cards/${card.jokerColor === "black" ? "black_joker.png" : "red_joker.png"}`;
  }
  return `/cards/${String(card.rank).toLowerCase()}_of_${card.suit}.png`;
}

function getCardPoints(card) {
  if (!card) return 0;
  if (card.rank === "joker") return 50;
  if (card.rank === "2" || card.rank === "A") return 20;
  if (["8", "9", "10", "J", "Q", "K"].includes(card.rank)) return 10;
  return 5;
}

function pluralize(count, singular, plural = `${singular}s`) {
  return `${count} ${count === 1 ? singular : plural}`;
}

function formatSignedNumber(value) {
  if (!value) return "0";
  return value > 0 ? `+${value}` : `${value}`;
}

function getScoreBreakdown(player, { finished = false, wentOut = false } = {}) {
  const meldCardPoints = (player?.melds || []).reduce(
    (total, meld) => total + (meld.cards || []).reduce((sum, card) => sum + getCardPoints(card), 0),
    0
  );
  const naturalCanastas = (player?.melds || []).filter((meld) => meld.isCanasta && meld.canastaType === "natural").length;
  const mixedCanastas = (player?.melds || []).filter((meld) => meld.isCanasta && meld.canastaType === "mixed").length;
  const blackThreeMelds = (player?.melds || []).filter((meld) => meld.specialType === "black3").length;
  const naturalCanastaBonus = naturalCanastas * 300;
  const mixedCanastaBonus = mixedCanastas * 200;
  const handPenalty = finished
    ? (player?.handPenalty || 0)
    : (player?.hand || []).reduce((sum, card) => sum + getCardPoints(card), 0);
  const redThreeBonus = player?.redThreeBonus || 0;
  const goingOutBonus = finished && wentOut ? 100 : 0;

  return {
    meldCardPoints,
    naturalCanastas,
    mixedCanastas,
    blackThreeMelds,
    naturalCanastaBonus,
    mixedCanastaBonus,
    redThreeCount: (player?.redThrees || []).length,
    redThreeBonus,
    handPenalty,
    goingOutBonus,
    total: meldCardPoints + naturalCanastaBonus + mixedCanastaBonus + redThreeBonus + goingOutBonus - handPenalty,
  };
}

function getActorLabel(players, actorId, localSocketId) {
  if (!actorId) return "A player";
  if (String(actorId) === String(localSocketId)) return "You";
  return (players || []).find((player) => String(player.socketId) === String(actorId))?.username || "A player";
}

function describeLastAction(lastAction, players, localSocketId) {
  if (!lastAction) return "Waiting for the first move.";

  const actor = getActorLabel(players, lastAction.by, localSocketId);

  if (lastAction.type === "drawStock") {
    const drawCount = lastAction.count || lastAction.cards?.length || 0;
    const redThreeCount = lastAction.redThrees?.length || 0;
    if (redThreeCount > 0) {
      return `${actor} drew ${pluralize(drawCount, "card")} from stock and tabled ${pluralize(redThreeCount, "red 3")} for replacements.`;
    }
    return `${actor} drew ${pluralize(drawCount, "card")} from stock.`;
  }

  if (lastAction.type === "takeDiscardPile") {
    return `${actor} picked up the discard pile by claiming the ${lastAction.rank}s and took ${pluralize(lastAction.pickedUp || 0, "card")} from the pile.`;
  }

  if (lastAction.type === "finishTurn") {
    if (lastAction.wentOut) {
      return `${actor} finished the turn with no cards left and went out.`;
    }
    return `${actor} finished the turn with no discard because no cards were left in hand.`;
  }

  if (lastAction.type === "createMeld") {
    return `${actor} created a new meld of ${pluralize(lastAction.count || 0, `${lastAction.rank}`)}.`;
  }

  if (lastAction.type === "addToMeld") {
    return `${actor} added ${pluralize(lastAction.count || 0, "card")} to the ${lastAction.rank} meld.`;
  }

  if (lastAction.type === "discard") {
    const cardText = getCardLabel(lastAction.card);
    if (lastAction.blockedByBlackThree) {
      return `${actor} discarded ${cardText}, blocking the discard pile with a black 3.`;
    }
    if (lastAction.frozen) {
      return `${actor} discarded ${cardText}, freezing the discard pile.`;
    }
    return `${actor} discarded ${cardText}.`;
  }

  return `${actor} made a play.`;
}

function validateNewMeld(cards, context = {}) {
  if (!Array.isArray(cards) || cards.length < 3) {
    return { ok: false, message: "Pick at least three cards." };
  }

  const allBlackThrees = cards.every((card) => isBlackThree(card));
  if (allBlackThrees) {
    if (!context.hasCanasta) {
      return { ok: false, message: "You need a completed canasta before melding black 3s." };
    }
    if (context.handLength !== cards.length) {
      return { ok: false, message: "Black 3s can only be melded as your final play when going out." };
    }
    return { ok: true, rank: "3", isBlackThreeMeld: true };
  }

  if (cards.some((card) => card.rank === "3")) {
    return { ok: false, message: "3s cannot be melded normally." };
  }

  const naturalCards = cards.filter((card) => !isWild(card));
  if (naturalCards.length < 2) {
    return { ok: false, message: "A new meld needs two natural cards." };
  }
  const rank = naturalCards[0]?.rank;
  if (!rank || rank === "2" || rank === "joker") {
    return { ok: false, message: "Wild cards cannot start a meld." };
  }
  if (naturalCards.some((card) => card.rank !== rank)) {
    return { ok: false, message: "Natural cards must share the same rank." };
  }
  const wildCount = cards.length - naturalCards.length;
  if (naturalCards.length <= wildCount) {
    return { ok: false, message: "You need more natural cards than wild cards." };
  }
  return { ok: true, rank };
}

function validateAddToMeld(meld, cards) {
  if (!meld) return { ok: false, message: "Choose a meld first." };
  if (meld.specialType === "black3") {
    return { ok: false, message: "Black 3 melds cannot be extended." };
  }
  if (!Array.isArray(cards) || cards.length === 0) {
    return { ok: false, message: "Select cards to add." };
  }
  const naturalCards = cards.filter((card) => !isWild(card));
  if (cards.some((card) => card.rank === "3")) {
    return { ok: false, message: "3s cannot be added to a normal meld." };
  }
  if (naturalCards.some((card) => card.rank !== meld.rank)) {
    return { ok: false, message: `Only ${meld.rank}s or wild cards can be added.` };
  }
  const newNaturalCount = (meld.naturalCount || 0) + naturalCards.length;
  const newWildCount = (meld.wildCount || 0) + (cards.length - naturalCards.length);
  if (newWildCount > 3) {
    return { ok: false, message: "A meld cannot contain more than 3 wild cards." };
  }
  if (newNaturalCount <= newWildCount) {
    return { ok: false, message: "That would leave too many wild cards in the meld." };
  }
  return { ok: true };
}

function CompactCard({ card, size = "hand", selected = false, faceDown = false, className = "" }) {
  const dimensions = {
    hand: "h-16 w-8 sm:h-18 sm:w-9",
    meld: "h-14 w-9 sm:h-16 sm:w-10",
    table: "h-16 w-8 sm:h-18 sm:w-9",
  }[size] || "h-16 w-8";

  if (faceDown) {
    return (
      <div
        className={`flex ${dimensions} items-center justify-center rounded-lg border shadow-sm ${
          selected ? "border-emerald-500 ring-2 ring-emerald-300/70" : "border-slate-400"
        } bg-slate-800 text-white ${className}`}
        style={{ boxShadow: "0 1px 2px rgba(15, 23, 42, 0.12)" }}
      >
        <div className="text-[10px] font-bold tracking-[0.2em] opacity-80 sm:text-xs">CAN</div>
      </div>
    );
  }

  const isJoker = card?.rank === "joker";
  const suit = card?.suit || "";
  const symbol = isJoker ? (card?.jokerColor === "black" ? "★" : "✦") : SUIT_SYMBOLS[suit] || "";
  const cornerSuit = isJoker ? "J" : SUIT_SYMBOLS[suit] || "";
  const rankText = isJoker ? "Jkr" : String(card?.rank || "");
  const isRed = isJoker ? card?.jokerColor !== "black" : suit === "hearts" || suit === "diamonds";
  const accentClass = isRed ? "text-rose-700" : "text-slate-900";

  return (
    <div
      className={`relative ${dimensions} overflow-hidden rounded-lg bg-white dark:bg-slate-50 ${
        selected ? "ring-2 ring-emerald-300/70" : ""
      } ${className}`}
      style={{ boxShadow: selected ? "0 0 0 1px rgb(16 185 129 / 0.85), 0 1px 2px rgba(15, 23, 42, 0.18)" : "0 1px 2px rgba(15, 23, 42, 0.14)" }}
    >
      <div className={`absolute left-1 top-1 flex flex-col items-start leading-none ${accentClass}`}>
        <span className="text-[8px] font-extrabold sm:text-[9px]">{rankText}</span>
        <span className="text-[8px] sm:text-[9px]">{cornerSuit}</span>
      </div>

      <div className={`absolute bottom-1 right-1 flex rotate-180 flex-col items-start leading-none ${accentClass}`}>
        <span className="text-[8px] font-extrabold sm:text-[9px]">{rankText}</span>
        <span className="text-[8px] sm:text-[9px]">{cornerSuit}</span>
      </div>

      <div className={`flex h-full w-full items-center justify-center ${accentClass}`}>
        {isJoker ? (
          <div className="flex flex-col items-center justify-center leading-none">
            <span className="text-base sm:text-lg">{symbol}</span>
            <span className="mt-0.5 text-[7px] font-bold uppercase tracking-[0.08em] sm:text-[8px]">
              {card?.jokerColor || "red"}
            </span>
          </div>
        ) : (
          <span className="text-lg leading-none opacity-90 sm:text-xl">{symbol}</span>
        )}
      </div>
    </div>
  );
}

function CardImage({ card, size = "hand", selected = false, faceDown = false, className = "" }) {
  const dimensions = {
    hand: "h-20 w-14 sm:h-24 sm:w-16",
    meld: "h-14 w-9 sm:h-16 sm:w-10",
    table: "h-20 w-14 sm:h-24 sm:w-16",
  }[size] || "h-20 w-14";

  if (faceDown) {
    return (
      <img
        src="/cards/card_back.png"
        alt="Card back"
        className={`${dimensions} rounded-xl bg-white p-1 object-contain dark:bg-transparent ${
          selected ? "ring-2 ring-emerald-300/70" : ""
        } ${className}`}
        draggable={false}
      />
    );
  }

  return (
    <img
      src={getCardImage(card)}
      alt={getCardLabel(card)}
      className={`${dimensions} rounded-xl bg-white p-1 object-contain dark:bg-transparent ${
        selected ? "ring-2 ring-emerald-300/70" : ""
      } ${className}`}
      draggable={false}
    />
  );
}

function ScoreBreakdownCard({ player, isCurrent, isLocal, wentOutPlayerId, finished }) {
  const breakdown = getScoreBreakdown(player, {
    finished,
    wentOut: String(player?.socketId) === String(wentOutPlayerId),
  });

  return (
    <details
      className={`rounded-2xl border px-2.5 py-2 sm:px-3 ${
        isCurrent
          ? "border-amber-400 bg-amber-50 text-amber-900 dark:border-amber-300 dark:bg-amber-400/10 dark:text-amber-100"
          : "border-slate-200 bg-white text-slate-700 dark:border-slate-700 dark:bg-slate-900/70 dark:text-slate-200"
      }`}
    >
      <summary className="cursor-pointer list-none">
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <div className="truncate text-[13px] font-semibold sm:text-sm">
              {player.username}
              {isLocal ? " (You)" : ""}
            </div>
            <div className="text-[11px] opacity-70 sm:text-xs">
              {player.hand?.length || 0} cards • {(player.melds || []).length} melds • {naturalCanastasAndMixedText(breakdown)}
            </div>
          </div>
          <div className="text-right">
            <div className="text-base font-bold sm:text-lg">{player.score || 0}</div>
            <div className="text-[10px] uppercase tracking-wide opacity-70 sm:text-[11px]">
              {finished ? "Final score" : "Current score"}
            </div>
          </div>
        </div>
      </summary>

      <div className="mt-3 space-y-1.5 rounded-2xl bg-white/70 p-2 text-[11px] dark:bg-slate-950/40 sm:text-xs">
        <div className="flex items-center justify-between gap-2">
          <span>Melded cards</span>
          <span className="font-semibold">{breakdown.meldCardPoints}</span>
        </div>
        <div className="flex items-center justify-between gap-2">
          <span>Natural canastas</span>
          <span className="font-semibold">{breakdown.naturalCanastas} • {formatSignedNumber(breakdown.naturalCanastaBonus)}</span>
        </div>
        <div className="flex items-center justify-between gap-2">
          <span>Mixed canastas</span>
          <span className="font-semibold">{breakdown.mixedCanastas} • {formatSignedNumber(breakdown.mixedCanastaBonus)}</span>
        </div>
        <div className="flex items-center justify-between gap-2">
          <span>Red 3s</span>
          <span className="font-semibold">{breakdown.redThreeCount} • {formatSignedNumber(breakdown.redThreeBonus)}</span>
        </div>
        {breakdown.blackThreeMelds > 0 && (
          <div className="flex items-center justify-between gap-2">
            <span>Black 3 melds</span>
            <span className="font-semibold">{breakdown.blackThreeMelds}</span>
          </div>
        )}
        {(finished || breakdown.handPenalty > 0) && (
          <div className="flex items-center justify-between gap-2">
            <span>Hand penalty</span>
            <span className="font-semibold">-{breakdown.handPenalty}</span>
          </div>
        )}
        {breakdown.goingOutBonus > 0 && (
          <div className="flex items-center justify-between gap-2">
            <span>Going out bonus</span>
            <span className="font-semibold">+{breakdown.goingOutBonus}</span>
          </div>
        )}
        <div className="mt-2 flex items-center justify-between gap-2 border-t border-slate-200 pt-2 font-semibold dark:border-slate-700">
          <span>Total</span>
          <span>{breakdown.total}</span>
        </div>
      </div>
    </details>
  );
}

function naturalCanastasAndMixedText(breakdown) {
  const parts = [];
  if (breakdown.naturalCanastas > 0) {
    parts.push(`${breakdown.naturalCanastas} natural`);
  }
  if (breakdown.mixedCanastas > 0) {
    parts.push(`${breakdown.mixedCanastas} mixed`);
  }
  if (parts.length === 0) {
    return "no canastas";
  }
  return `${parts.join(" / ")} canastas`;
}

function PileCountBadge({ count }) {
  return (
    <span className="absolute -right-1 -top-1 z-10 inline-flex min-w-[1.6rem] items-center justify-center rounded-full bg-slate-900 px-1.5 py-0.5 text-[10px] font-bold leading-none text-white shadow-sm dark:bg-slate-100 dark:text-slate-900 sm:min-w-[1.8rem] sm:text-[11px]">
      {count}
    </span>
  );
}

function MeldStrip({ meld }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-2 dark:border-slate-700 dark:bg-slate-900/60">
      <div className="mb-1 flex items-center justify-between gap-2 text-[11px] text-slate-500 dark:text-slate-400 sm:text-xs">
        <span className="font-semibold text-slate-700 dark:text-slate-100">{meld.rank}s</span>
        <span>
          {meld.specialType === "black3"
            ? `${meld.cards.length} cards • black 3s`
            : `${meld.cards.length} cards${meld.isCanasta ? ` • ${meld.canastaType}` : ""}`}
        </span>
      </div>
      <div className="flex min-w-0 overflow-x-auto pb-1">
        {(meld.cards || []).map((card, index) => (
          <div key={card.id || `${meld.rank}-${index}`} className={`${index === 0 ? "" : "-ml-4 sm:-ml-6"} shrink-0`}>
            <CompactCard card={card} size="meld" />
          </div>
        ))}
      </div>
    </div>
  );
}

function HandCard({ card, selected, isNew = false, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`relative h-20 w-14 shrink-0 rounded-xl transition-transform sm:h-24 sm:w-16 ${selected ? "-translate-y-2" : "translate-y-0"}`}
      title={getCardLabel(card)}
    >
      <CardImage card={card} size="table" selected={selected} className="h-full w-full" />
      {isNew && (
        <span className="absolute bottom-1 left-1 flex h-4 w-4 items-center justify-center rounded-full bg-amber-300 text-[12px] font-black leading-none text-amber-950 shadow-sm dark:bg-amber-300 dark:text-amber-950 sm:h-5 sm:w-5 sm:text-[13px]">
          *
        </span>
      )}
      {isWild(card) && (
        <span className="absolute right-1 top-1 rounded-full bg-rose-500 px-1 py-0.5 text-[9px] font-bold text-white sm:px-1.5 sm:text-[10px]">
          Wild
        </span>
      )}
    </button>
  );
}

function GroupedHand({ cards, selectedIds, newCardIds, onToggle, onToggleGroup }) {
  const groups = useMemo(() => {
    const orderedKeys = [];
    const grouped = new Map();
    for (const card of cards || []) {
      const key = card.rank === "joker" ? "joker" : card.rank;
      if (!grouped.has(key)) {
        grouped.set(key, []);
        orderedKeys.push(key);
      }
      grouped.get(key).push(card);
    }
    return orderedKeys.map((key) => ({
      key,
      cards: grouped.get(key) || [],
    }));
  }, [cards]);

  return (
    <div className="flex flex-wrap gap-x-1 gap-y-2 px-1 pt-2 sm:gap-x-2 sm:gap-y-3 sm:pt-3">
      {groups.map((group) => {
        const cardOffset = 18;
        const singleWidth = 56;
        const singleHeight = 80;
        const stackWidth = singleWidth + Math.max(0, group.cards.length - 1) * cardOffset;
        return (
          <div
            key={group.key}
            className="relative shrink-0"
            style={{ width: `${stackWidth}px`, height: `${singleHeight}px` }}
            title={`${group.key} (${group.cards.length})`}
          >
            {group.cards.map((card, index) => (
              <div
                key={card.id}
                className="absolute top-0"
                style={{ left: `${index * cardOffset}px`, zIndex: index + 1 }}
              >
                <HandCard
                  card={card}
                  selected={selectedIds.includes(card.id)}
                  isNew={newCardIds.includes(String(card.id))}
                  onClick={() => onToggle(card.id)}
                />
              </div>
            ))}
            {group.cards.length > 1 && (
              <button
                type="button"
                onClick={() => onToggleGroup(group.cards.map((card) => card.id))}
                className="absolute -right-1 -top-1 z-20 rounded-full bg-blue-600 px-1.5 py-0.5 text-[10px] font-bold text-white transition-colors hover:bg-blue-500 dark:bg-blue-900 dark:text-blue-100 dark:hover:bg-blue-800"
                title={`Toggle all ${group.cards.length} ${group.key}${group.cards.length === 1 ? "" : "s"}`}
              >
                {group.cards.length}
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

function CanastaGame({ gameState, gamePlayers = [], user = {}, socket, roomId, isSpectator = false }) {
  // --- Spectator Perspective logic (modular, opt-in) ---
  const perspectiveOptions = useMemo(() => {
    if (Array.isArray(gamePlayers) && gamePlayers.length > 0) return gamePlayers;
    if (Array.isArray(gameState?.players) && gameState.players.length > 0) return gameState.players;
    return [];
  }, [gamePlayers, gameState?.players]);

  const [spectatorViewPlayerId, setSpectatorViewPlayerId] = useState(null);
  const perspectivePlayer = useMemo(() => {
    if (!isSpectator || !spectatorViewPlayerId) return null;
    return perspectiveOptions.find((player) => {
      const playerId = player?.userId || player?.socketId || player?.id || player?.username;
      return String(playerId) === String(spectatorViewPlayerId);
    }) || null;
  }, [perspectiveOptions, isSpectator, spectatorViewPlayerId]);

  useEffect(() => {
    if (!isSpectator) {
      setSpectatorViewPlayerId(null);
      return;
    }
    if (!Array.isArray(perspectiveOptions) || perspectiveOptions.length === 0) {
      setSpectatorViewPlayerId(null);
      return;
    }
    const exists = perspectiveOptions.some((player) => {
      const playerId = player?.userId || player?.socketId || player?.id || player?.username;
      return String(playerId) === String(spectatorViewPlayerId);
    });
    if (!exists) {
      const first = perspectiveOptions[0];
      setSpectatorViewPlayerId(first?.userId || first?.socketId || first?.id || first?.username || null);
    }
  }, [perspectiveOptions, isSpectator, spectatorViewPlayerId]);

  const { submitAction, perspectiveUser, perspectiveIds } = useGameBase({ gameState, gamePlayers, user, socket, roomId, perspectivePlayer });
  const [selectedIds, setSelectedIds] = useState([]);
  const [feedback, setFeedback] = useState("");
  const [newCardIds, setNewCardIds] = useState([]);
  const previousHandIdsRef = useRef([]);
  const processedDrawActionAtRef = useRef(null);

  const matchesPerspectivePlayer = useMemo(() => {
    const idSet = new Set((perspectiveIds || []).filter(Boolean).map((value) => String(value)));
    return (player) => {
      if (!player || idSet.size === 0) return false;
      return [player.socketId, player.userId, player.id, player.username]
        .filter((value) => value !== undefined && value !== null)
        .some((value) => idSet.has(String(value)));
    };
  }, [perspectiveIds]);

  const localSocketId = useMemo(() => {
    const fromState = (gameState?.players || []).find(matchesPerspectivePlayer);
    if (fromState?.socketId) return fromState.socketId;
    return perspectiveUser?.socketId || null;
  }, [gameState?.players, matchesPerspectivePlayer, perspectiveUser?.socketId]);

  const me = useMemo(() => {
    return (gameState?.players || []).find(matchesPerspectivePlayer) || null;
  }, [gameState?.players, matchesPerspectivePlayer]);

  const otherPlayers = useMemo(() => {
    return (gameState?.players || []).filter((player) => !matchesPerspectivePlayer(player));
  }, [gameState?.players, matchesPerspectivePlayer]);

  const currentPlayerId = gameState?.currentPlayerSocketId || gameState?.currentPlayer || null;
  const isMyTurn = !!me && String(me.socketId) === String(currentPlayerId) && !gameState?.finished;
  const phase = gameState?.phase || "draw";
  const topDiscard = gameState?.discardPile?.[gameState.discardPile.length - 1] || null;
  const currentPlayerName = (gameState?.players || []).find((player) => String(player.socketId) === String(currentPlayerId))?.username || "Another player";
  const stockDrawCount = (gameState?.players || []).length >= 3 ? 1 : 2;

  const selectedCards = useMemo(() => {
    const wanted = new Set(selectedIds.map(String));
    return (me?.hand || []).filter((card) => wanted.has(String(card.id)));
  }, [me?.hand, selectedIds]);

  const selectedNewMeld = useMemo(
    () => validateNewMeld(selectedCards, { handLength: me?.hand?.length || 0, hasCanasta: !!me?.hasCanasta }),
    [selectedCards, me?.hand?.length, me?.hasCanasta]
  );

  const addableMeldRanks = useMemo(() => {
    return (me?.melds || [])
      .filter((meld) => validateAddToMeld(meld, selectedCards).ok)
      .map((meld) => meld.rank);
  }, [me?.melds, selectedCards]);

  useEffect(() => {
    setSelectedIds([]);
    setFeedback("");
  }, [gameState?.turnNumber, gameState?.phase, currentPlayerId]);

  useEffect(() => {
    const currentHandIds = (me?.hand || []).map((card) => String(card.id));
    const lastAction = gameState?.lastAction;
    const shouldMarkNewCards =
      !!lastAction &&
      lastAction.at !== processedDrawActionAtRef.current &&
      ["drawStock", "takeDiscardPile"].includes(lastAction.type) &&
      String(lastAction.by) === String(localSocketId);

    if (shouldMarkNewCards) {
      const previousIds = new Set(previousHandIdsRef.current);
      setNewCardIds(currentHandIds.filter((id) => !previousIds.has(id)));
      processedDrawActionAtRef.current = lastAction.at;
    }

    previousHandIdsRef.current = currentHandIds;
  }, [me?.hand, gameState?.lastAction, localSocketId]);

  useEffect(() => {
    if (!isMyTurn || phase === "draw" || gameState?.finished) {
      setNewCardIds([]);
    }
  }, [gameState?.finished, isMyTurn, phase, gameState?.turnNumber]);

  const toggleCard = (cardId) => {
    setSelectedIds((previous) =>
      previous.includes(cardId) ? previous.filter((id) => id !== cardId) : [...previous, cardId]
    );
  };

  const toggleCardGroup = (cardIds) => {
    const normalizedIds = (cardIds || []).map(String);
    setSelectedIds((previous) => {
      const previousSet = new Set(previous.map(String));
      const allSelected = normalizedIds.every((id) => previousSet.has(id));

      if (allSelected) {
        return previous.filter((id) => !normalizedIds.includes(String(id)));
      }

      const next = [...previous];
      for (const id of normalizedIds) {
        if (!previousSet.has(id)) {
          next.push(id);
        }
      }
      return next;
    });
  };

  const sendAction = async (action) => {
    setFeedback("");
    const response = await submitAction(action);
    if (response?.error) {
      setFeedback(response.error);
      return;
    }
    if (response?.status && response.status !== "ok" && response.status !== "initialized") {
      setFeedback(response.error || response.status.replace(/-/g, " "));
      return;
    }
    setSelectedIds([]);
  };

  const showSpectatorPerspectiveSelector = isSpectator && perspectiveOptions.length > 0;

  if (!gameState || !me) {
    return (
      <div className="mx-auto w-full max-w-[26rem] space-y-2 p-2 text-slate-800 dark:text-slate-100 sm:max-w-5xl sm:space-y-3 sm:p-3">
        {showSpectatorPerspectiveSelector && (
          <SpectatorPerspectiveSelector
            players={perspectiveOptions}
            value={spectatorViewPlayerId || ""}
            onChange={setSpectatorViewPlayerId}
          />
        )}
        <div className="p-4 text-slate-600 dark:text-slate-300">Loading Canasta...</div>
      </div>
    );
  }

  const discardNeedsPair = !!topDiscard && !isWild(topDiscard) && (gameState?.discardFrozen || !(me.melds || []).some((meld) => meld.rank === topDiscard.rank));
  const matchingNaturalCount = selectedCards.filter((card) => !isWild(card) && card.rank === topDiscard?.rank).length;
  const canTakeDiscard =
    !!topDiscard &&
    !isWild(topDiscard) &&
    !isBlackThree(topDiscard) &&
    !isRedThree(topDiscard) &&
    (gameState?.discardFrozen ? matchingNaturalCount >= 2 : ((me.melds || []).some((meld) => meld.rank === topDiscard.rank) || matchingNaturalCount >= 2));
  const activitySummary = describeLastAction(gameState?.lastAction, gameState?.players || [], localSocketId);
  const turnSummary = !gameState.finished
    ? (phase === "draw"
      ? `${isMyTurn ? "You" : currentPlayerName} need${isMyTurn ? "" : "s"} to draw ${stockDrawCount} ${stockDrawCount === 1 ? "card" : "cards"} from stock or claim the discard pile.`
      : `${isMyTurn ? "You" : currentPlayerName} ${isMyTurn ? "can" : "can"} meld, extend melds, or discard to end the turn.`)
    : null;
  const roundEndSummary = gameState.finished && gameState?.result?.breakdown?.reason
    ? `Round ended because ${gameState.result.breakdown.reason.replace(/-/g, " ")}.`
    : null;
  const controlsHeadline = gameState.finished ? "Round summary" : isMyTurn ? "Your turn" : `${currentPlayerName}'s turn`;
  const topDiscardSummary = topDiscard
    ? `${getCardLabel(topDiscard)}${
        isBlackThree(topDiscard)
          ? " • black 3s block the discard pile"
          : discardNeedsPair && !canTakeDiscard
            ? ` • select 2 natural ${topDiscard.rank}s to claim it`
            : ""
      }`
    : null;
  const availableActions = [];

  if (isMyTurn && phase === "draw" && (gameState.stockPile?.length || 0) > 0) {
    availableActions.push({
      key: "draw-stock",
      label: `Draw ${stockDrawCount}`,
      onClick: () => sendAction({ type: "drawStock" }),
      className: "bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900",
    });
  }

  if (isMyTurn && phase === "draw" && canTakeDiscard) {
    availableActions.push({
      key: "take-discard",
      label: "Take discard pile",
      onClick: () => sendAction({ type: "takeDiscardPile", cardIds: selectedIds }),
      className: "bg-emerald-600 text-white",
    });
  }

  if (isMyTurn && phase === "meld" && selectedNewMeld.ok) {
    availableActions.push({
      key: "create-meld",
      label: "Create meld",
      onClick: () => sendAction({ type: "createMeld", cardIds: selectedIds }),
      className: "bg-indigo-600 text-white",
    });
  }

  if (isMyTurn && phase === "meld") {
    (me.melds || []).forEach((meld) => {
      const validation = validateAddToMeld(meld, selectedCards);
      if (validation.ok) {
        availableActions.push({
          key: `add-${meld.rank}`,
          label: `Add to ${meld.rank}s`,
          onClick: () => sendAction({ type: "addToMeld", rank: meld.rank, cardIds: selectedIds }),
          className: "bg-amber-500 text-white",
        });
      }
    });
  }

  if (isMyTurn && phase === "meld" && selectedIds.length === 1) {
    availableActions.push({
      key: "discard-selected",
      label: "Discard selected",
      onClick: () => sendAction({ type: "discard", cardId: selectedIds[0] }),
      className: "bg-rose-600 text-white",
    });
  }

  if (isMyTurn && phase === "meld" && me.hand.length === 0) {
    availableActions.push({
      key: "finish-turn",
      label: "Finish Turn",
      onClick: () => sendAction({ type: "finishTurn" }),
      className: "bg-sky-600 text-white",
    });
  }

  const nextStepSummary = gameState.finished
    ? "No actions remain. The round is complete."
    : isMyTurn
      ? (phase === "draw"
          ? (canTakeDiscard
              ? "Choose whether to draw from stock or take the discard pile."
              : `Draw ${stockDrawCount} ${stockDrawCount === 1 ? "card" : "cards"} from stock to start your turn.`)
          : me.hand.length === 0
            ? "Finish your turn to pass play on."
            : selectedCards.length === 0
              ? "Select cards from your hand to create a meld, add to a meld, or choose one to discard."
              : (selectedNewMeld.message || "Adjust your selection so it forms a valid meld or a single discard."))
      : `Waiting for ${currentPlayerName} to play.`;

  return (
    <div className="mx-auto w-full max-w-[26rem] space-y-2 p-2 text-slate-800 dark:text-slate-100 sm:max-w-5xl sm:space-y-3 sm:p-3">
      {showSpectatorPerspectiveSelector && (
        <SpectatorPerspectiveSelector
          players={perspectiveOptions}
          value={spectatorViewPlayerId || ""}
          onChange={setSpectatorViewPlayerId}
        />
      )}
      <details className="rounded-3xl border border-slate-200 bg-gradient-to-br from-slate-50 via-white to-emerald-50 p-3 shadow-sm dark:border-slate-700 dark:from-slate-950 dark:via-slate-900 dark:to-emerald-950/40 sm:p-4">
        <summary className="cursor-pointer list-none">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400 sm:text-xs sm:tracking-[0.2em]">
              Scores
            </div>
            <span className="text-[11px] text-slate-500 dark:text-slate-400">Tap to expand</span>
            <div className="flex flex-wrap items-center gap-1.5 text-xs sm:gap-2 sm:text-sm">
              {gameState.discardFrozen && (
                <span className="rounded-full bg-rose-100 px-2.5 py-1 font-semibold text-rose-700 dark:bg-rose-500/15 dark:text-rose-200 sm:px-3">
                  Discard frozen
                </span>
              )}
            </div>
          </div>
        </summary>
        <div className="mt-3 space-y-2">
          <ScoreBreakdownCard
            player={me}
            isCurrent={isMyTurn}
            isLocal
            wentOutPlayerId={gameState?.result?.breakdown?.wentOutPlayerId}
            finished={!!gameState.finished}
          />
          {otherPlayers.map((player) => (
            <ScoreBreakdownCard
              key={player.socketId}
              player={player}
              isCurrent={String(player.socketId) === String(currentPlayerId)}
              isLocal={false}
              wentOutPlayerId={gameState?.result?.breakdown?.wentOutPlayerId}
              finished={!!gameState.finished}
            />
          ))}
        </div>
      </details>

      <div className="flex flex-col xl:flex-col gap-2 sm:gap-3">
        <div className="space-y-3">
          <div className="rounded-3xl border border-slate-200 bg-white p-2.5 shadow-sm dark:border-slate-700 dark:bg-slate-900/80 sm:p-3">
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2 sm:mb-3">
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400 sm:text-xs sm:tracking-[0.2em]">
                  Table
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-1.5 text-xs sm:gap-2 sm:text-sm">
                <span className="rounded-lg border border-slate-300 bg-slate-100 px-2 py-1 font-medium text-slate-700 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 sm:px-2.5">
                  Turn {gameState.turnNumber}
                </span>
                <span className="rounded-lg border border-emerald-200 bg-emerald-50 px-2 py-1 font-medium text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-200 sm:px-2.5">
                  {phase === "draw" ? "Draw phase" : gameState.finished ? "Finished" : "Meld / discard"}
                </span>
                {gameState.finished && (
                  <div className="rounded-full bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-700 dark:bg-amber-500/15 dark:text-amber-200 sm:px-3 sm:text-sm">
                    {(gameState.winners || []).includes(me.socketId) ? "You won" : "Round over"}
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2 sm:gap-3">
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-2 text-center dark:border-slate-700 dark:bg-slate-950 sm:p-3">
                <div className="text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400 sm:text-xs">Stock</div>
                <div className="mt-1.5 flex items-center justify-center sm:mt-2">
                  <div className="relative inline-flex">
                    <CardImage faceDown size="table" />
                    <PileCountBadge count={gameState.stockPile?.length || 0} />
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-2 text-center dark:border-slate-700 dark:bg-slate-950 sm:p-3">
                <div className="text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400 sm:text-xs">Discard</div>
                <div className="mt-1.5 flex items-center justify-center sm:mt-2">
                  <div className="relative inline-flex">
                    {topDiscard ? (
                      <CardImage card={topDiscard} size="table" />
                    ) : (
                      <div className="flex h-20 w-14 items-center justify-center rounded-xl border border-dashed border-slate-300 text-[10px] text-slate-400 dark:border-slate-600 sm:h-24 sm:w-16 sm:text-xs">
                        Empty
                      </div>
                    )}
                    <PileCountBadge count={gameState.discardPile?.length || 0} />
                  </div>
                </div>
              </div>

            </div>
          </div>

          <div className="rounded-3xl border border-slate-200 bg-white p-2.5 shadow-sm dark:border-slate-700 dark:bg-slate-900/80 sm:p-3">
            <div className="mb-2 sm:mb-3">
              <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400 sm:text-xs sm:tracking-[0.2em]">
                Controls
              </div>
            </div>

            <div className="mb-3 rounded-2xl border border-emerald-200 bg-emerald-50/70 p-2 dark:border-emerald-900 dark:bg-emerald-950/30 sm:p-2.5">
              <div className="text-xs font-semibold text-emerald-900 dark:text-emerald-100 sm:text-sm">
                {controlsHeadline} - {activitySummary}
              </div>
              {turnSummary && (
                <div className="mt-1 bg-transparent text-xs text-emerald-900 dark:bg-transparent dark:text-emerald-100 sm:mt-1.5 sm:text-sm">
                  {turnSummary}
                </div>
              )}
              {roundEndSummary && (
                <div className="mt-1 bg-transparent text-xs text-emerald-900 dark:bg-transparent dark:text-emerald-100 sm:mt-1.5 sm:text-sm">
                  {roundEndSummary}
                </div>
              )}
              {topDiscardSummary && (
                <div className="mt-1 bg-transparent text-xs dark:bg-transparent sm:mt-1.5 sm:text-sm">
                  <span className="font-semibold">Top discard:</span> {topDiscardSummary}
                </div>
              )}
            </div>

            {availableActions.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {availableActions.map((action) => (
                  <button
                    key={action.key}
                    type="button"
                    onClick={action.onClick}
                    className={`rounded-full px-3 py-2 text-xs font-semibold sm:px-4 sm:text-sm ${action.className}`}
                  >
                    {action.label}
                  </button>
                ))}
              </div>
            ) : (
              <div className="rounded-2xl bg-slate-50 px-3 py-2 text-xs text-slate-500 dark:bg-slate-950/60 dark:text-slate-400 sm:text-sm">
                {nextStepSummary}
              </div>
            )}
          </div>

          <div className="rounded-3xl border border-slate-200 bg-white p-2.5 shadow-sm dark:border-slate-700 dark:bg-slate-900/80 sm:p-3">
            <div className="mb-2 flex items-center justify-between gap-2 sm:mb-3">
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400 sm:text-xs sm:tracking-[0.2em]">
                  Your Hand
                </div>
                <div className="text-base font-bold sm:text-lg">Tap cards to build plays</div>
              </div>
              <button
                type="button"
                onClick={() => setSelectedIds([])}
                className="rounded-full border border-slate-300 px-3 py-1.5 text-[11px] font-semibold text-slate-600 dark:border-slate-600 dark:text-slate-200 sm:text-xs"
              >
                Clear selection
              </button>
            </div>

            <div className="overflow-hidden pb-2">
              <GroupedHand
                cards={me.hand || []}
                selectedIds={selectedIds}
                newCardIds={newCardIds}
                onToggle={toggleCard}
                onToggleGroup={toggleCardGroup}
              />
            </div>

            {me.hand.length === 1 && !me.hasCanasta && phase === "meld" && isMyTurn && (
              <div className="mt-2 rounded-2xl bg-rose-50 px-3 py-2 text-xs text-rose-700 dark:bg-rose-500/10 dark:text-rose-200 sm:mt-3 sm:text-sm">
                You can discard your last card and keep playing, but you still need a completed canasta before you can go out and end the round.
              </div>
            )}

            {me.hand.length === 0 && phase === "meld" && isMyTurn && (
              <div className="mt-2 rounded-2xl bg-sky-50 px-3 py-2 text-xs text-sky-700 dark:bg-sky-500/10 dark:text-sky-200 sm:mt-3 sm:text-sm">
                No cards are left in your hand. Press Finish Turn to move play on{me.hasCanasta ? " and go out." : "."}
              </div>
            )}
          </div>

          <div className="rounded-3xl border border-slate-200 bg-white p-2.5 shadow-sm dark:border-slate-700 dark:bg-slate-900/80 sm:p-3">
            <div className="flex items-center justify-between gap-2">
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400 sm:text-xs sm:tracking-[0.2em]">
                  Your Melds
                </div>
              </div>
              <div className="text-xs text-slate-500 dark:text-slate-400 sm:text-sm">{(me.melds || []).length} total</div>
            </div>

            <div className="mt-3">
              {!!(me.redThrees || []).length && (
                <div className="mb-3 rounded-2xl border border-rose-200 bg-rose-50 p-3 dark:border-rose-900 dark:bg-rose-950/30">
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <div className="text-sm font-semibold text-rose-800 dark:text-rose-100">Red 3s</div>
                    <div className="text-xs text-rose-700 dark:text-rose-200">
                      {me.redThrees.length} • {me.redThreeBonus || 0} bonus
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {(me.redThrees || []).map((card) => (
                      <CompactCard key={card.id} card={card} size="meld" />
                    ))}
                  </div>
                </div>
              )}

              {(me.melds || []).length === 0 ? (
                <div className="rounded-2xl border border-dashed border-slate-300 p-3 text-xs text-slate-500 dark:border-slate-700 dark:text-slate-400 sm:p-4 sm:text-sm">
                  No melds yet. Draw, then lay down a rank set of at least three cards.
                </div>
              ) : (
                <div className="grid gap-2 md:grid-cols-2">
                  {me.melds.map((meld) => (
                    <MeldStrip key={meld.rank} meld={meld} />
                  ))}
                </div>
              )}
              {selectedCards.length > 0 && (
                <div className="mt-2 rounded-2xl bg-slate-100 px-3 py-2 text-xs text-slate-700 dark:bg-slate-800 dark:text-slate-200 sm:mt-3 sm:text-sm">
                  {selectedCards.length} selected
                  {selectedNewMeld.ok
                    ? ` • new ${selectedNewMeld.rank}s meld ready`
                    : addableMeldRanks.length > 0
                      ? ` • add to ${addableMeldRanks.join(", ")} meld${addableMeldRanks.length === 1 ? "" : "s"} ready`
                      : selectedNewMeld.message
                        ? ` • ${selectedNewMeld.message}`
                        : ""}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="space-y-3">
          <details className="rounded-3xl border border-slate-200 bg-white p-2.5 shadow-sm dark:border-slate-700 dark:bg-slate-900/80 sm:p-3">
            <summary className="cursor-pointer list-none">
              <div className="flex items-center justify-between gap-3">
                <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400 sm:text-xs sm:tracking-[0.2em]">
                  Opponents and Status
                </span>
                <span className="text-xs font-medium text-slate-500 dark:text-slate-400">
                  {otherPlayers.length} players
                </span>
              </div>
            </summary>

            <div className="mt-3 space-y-3">
              {otherPlayers.length === 0 ? (
                <div className="text-xs text-slate-500 dark:text-slate-400 sm:text-sm">Waiting for another player.</div>
              ) : (
                otherPlayers.map((player) => (
                  <div key={player.socketId} className="rounded-2xl border border-slate-200 p-2.5 dark:border-slate-700 sm:p-3">
                    <div className="mb-2 flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="truncate text-sm font-semibold">{player.username}</div>
                        <div className="text-[11px] text-slate-500 dark:text-slate-400 sm:text-xs">
                          {player.hand?.length || 0} cards • {player.score || 0} points
                        </div>
                      </div>
                      {String(player.socketId) === String(currentPlayerId) && (
                        <span className="rounded-full bg-amber-100 px-2 py-1 text-[10px] font-semibold text-amber-700 dark:bg-amber-500/15 dark:text-amber-200 sm:text-[11px]">
                          Active
                        </span>
                      )}
                    </div>

                    {(player.melds || []).length === 0 ? (
                      <div className="text-xs text-slate-500 dark:text-slate-400 sm:text-sm">No melds on the table.</div>
                    ) : (
                      <div className="space-y-2">
                        {player.melds.map((meld) => (
                          <MeldStrip key={`${player.socketId}-${meld.rank}`} meld={meld} />
                        ))}
                      </div>
                    )}
                  </div>
                ))
              )}

              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-2.5 dark:border-slate-700 dark:bg-slate-950/50 sm:p-3">
                <div className="mb-2 text-sm font-semibold">Round status</div>
                <div className="grid grid-cols-2 gap-2 text-xs text-slate-600 dark:text-slate-300 sm:text-sm">
                  <div>Discard size: <span className="font-semibold">{gameState.discardPile?.length || 0}</span></div>
                  <div>Your canastas: <span className="font-semibold">{(me.melds || []).filter((meld) => meld.isCanasta).length}</span></div>
                  {gameState.result?.breakdown?.reason && (
                    <div className="col-span-2">End condition: <span className="font-semibold">{gameState.result.breakdown.reason.replace(/-/g, " ")}</span></div>
                  )}
                </div>
              </div>
            </div>
          </details>
        </div>
      </div>

      {feedback && (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-xs text-rose-700 dark:border-rose-900 dark:bg-rose-950/40 dark:text-rose-200 sm:text-sm">
          {feedback}
        </div>
      )}
    </div>
  );
}

CanastaGame.Guide = function CanastaGuide() {
  return (
    <div className="space-y-2 text-sm text-slate-700 dark:text-slate-200">
      <h3 className="text-base font-bold">How to Play Canasta</h3>
      <p>
        This is a compact, faster Canasta variant built for the room view. In a 2-player game you draw 2 cards from stock; with 3 or more players you draw 1. You can also claim the discard pile if you can use its top card immediately.
      </p>
      <ul className="list-disc space-y-1 pl-5">
        <li>Red 3s are laid down immediately and replaced with a fresh draw.</li>
        <li>Red 3s score a bonus at the end of the round, or a penalty if you never got melds down.</li>
        <li>Meld cards of the same rank. Wild cards are 2s and jokers.</li>
        <li>A new meld needs at least 3 cards, including at least 2 natural cards.</li>
        <li>You must always keep more natural cards than wild cards in a meld.</li>
        <li>Black 3s block the discard pile when discarded, and they can only be melded as your final play when going out.</li>
        <li>Once a meld reaches 7 cards it becomes a canasta. Natural canastas score more than mixed ones.</li>
        <li>You need at least one completed canasta before going out and ending the round, but you may still discard your last card and keep playing if you do not have one yet.</li>
        <li>Discarding a wild card freezes the pile until someone claims it with two natural matching cards.</li>
        <li>Card values: jokers are 50, 2s and aces are 20, 8 through king are 10, and 4 through 7 are 5.</li>
        <li>Scoring: your melded cards add to your total, a mixed canasta adds 200, a natural canasta adds 300, and going out adds 100.</li>
        <li>At round end, cards left in your hand count against you, while red 3s are worth +100 each, or +800 for all four, if you got melds down; otherwise they count as a penalty instead.</li>
      </ul>
    </div>
  );
};

export default CanastaGame;